import React, { useCallback, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import Dataset from "datasets/types/interfaces/dataset";
import { AlertCircle, MessageSquareShare, Trash2 } from "lucide-react";
import useSWR, { mutate } from "swr";
import { DropdownMenuItem } from "~/components/ui/dropdown-menu";
import useToast from "~/components/ui/toast";
import useNextGenHeader from "~/contexts/nextgen-header";
import useUser from "~/contexts/user";
import { useFetcher } from "~/hooks/useFetcher";
import { buildEntityBreadcrumbs } from "~/pages/manifold-nextgen/breadcrumbs";
import { SettingsMenu } from "~/pages/manifold-nextgen/components/SettingsMenu";
import { buildExpandedAccess } from "~/pages/manifold-nextgen/components/share/AccessDetailsView";
import { ShareButton } from "~/pages/manifold-nextgen/components/share/ShareButton";
import { ShareModal } from "~/pages/manifold-nextgen/components/share/ShareModal";
import { useProjectShare } from "~/pages/manifold-nextgen/hooks/useProjectShare";
import { nextgenProjectPath, nextgenProjectsListPath } from "~/pages/manifold-nextgen/paths";
import { Agent, agentPath } from "~/services/agents";
import {
  Project,
  ProjectConversation,
  ShareLevel,
  deleteProject,
  projectAgentsPath,
  projectConversationsPath,
  projectDatasetsPath,
  projectListPath,
  projectPath,
} from "~/services/projects";
import { apiClient } from "~/utils/apiClient";
import { DeleteProjectDialog } from "./DeleteProjectDialog";
import { ProjectSidebar } from "./ProjectSidebar";
import { ShareConversationDialog } from "./ShareConversationDialog";

const { VITE_CATALOG_API } = import.meta.env;
const CATALOG_DATASETS_URL = `${VITE_CATALOG_API}/datasets?offset=0&limit=1000&sort_by=title&sort_order=asc`;

export interface ProjectOutletContext {
  projectId: string;
  project: Project;
  // Whether the caller owns (can_manage) this project. Gates owner-only controls.
  isOwner: boolean;
  sessions: ProjectConversation[] | undefined;
  attachedAgents: Agent[] | undefined;
  attachedAgentsWithDetails: Agent[] | undefined;
  attachedDatasets: Dataset[] | undefined;
}

export const ProjectLayout = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = useUser();
  const { showError } = useToast();
  const [shareOpen, setShareOpen] = useState(false);
  const [projectShareOpen, setProjectShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const {
    collaborators,
    isLoading: collaboratorsLoading,
    collaboratorsError,
    handleShare,
    accessLevels,
    defaultAccessLevel,
  } = useProjectShare(projectId ?? "");

  const memberCount = collaborators
    ? buildExpandedAccess(collaborators.users, collaborators.groups, accessLevels).length
    : undefined;

  const {
    data: project,
    error,
    isLoading,
  } = useFetcher<Project>(projectId ? projectPath(projectId) : null);

  const { data: attachedAgents } = useFetcher<Agent[]>(
    projectId ? projectAgentsPath(projectId) : null
  );

  const agentIds = attachedAgents?.map((a) => a.id);
  // Fetches full agent details (including starter_prompts) individually because
  // GET /projects/:id/agents returns a lightweight shape without that field.
  // Ideally starter_prompts would be included in that endpoint to avoid N requests.
  const { data: agentsWithDetails } = useSWR<Agent[]>(
    agentIds?.length ? ["project-agents-full", ...[...agentIds].sort()] : null,
    ([, ...ids]: [string, ...string[]]) =>
      Promise.all(ids.map((id) => apiClient<Agent>(agentPath(id))))
  );

  const { data: attachedDatasetIds } = useFetcher<string[]>(
    projectId ? projectDatasetsPath(projectId) : null
  );

  const { data: catalogDatasets } = useFetcher<Dataset[]>(CATALOG_DATASETS_URL);

  const { data: sessionsList } = useFetcher<{ items: ProjectConversation[] }>(
    projectId ? projectConversationsPath(projectId) : null
  );
  const sessions = sessionsList?.items;

  const attachedDatasets = useMemo<Dataset[] | undefined>(() => {
    if (attachedDatasetIds === undefined || catalogDatasets === undefined) {
      return undefined;
    }
    const byId = new Map(catalogDatasets.map((d) => [d.id, d]));
    return attachedDatasetIds.map((id) => byId.get(id)).filter((d): d is Dataset => Boolean(d));
  }, [attachedDatasetIds, catalogDatasets]);

  const conversationId = location.pathname.match(/\/c\/([^/]+)/)?.[1];
  const currentSession = conversationId
    ? sessions?.find((s) => s.id === conversationId)
    : undefined;

  // Label for the active sub-page (deeper than the project Overview root) so the
  // breadcrumb shows where you are and the project name links back to Overview.
  const subPageLabel = conversationId
    ? (currentSession?.display_name ?? "Loading…")
    : location.pathname.endsWith("/data")
      ? "Datasets"
      : location.pathname.endsWith("/agents")
        ? "Agents"
        : location.pathname.endsWith("/chats")
          ? "New Session"
          : undefined;

  const breadcrumbs = useMemo(
    () =>
      buildEntityBreadcrumbs({
        rootHref: nextgenProjectsListPath(),
        entityName: project?.name ?? "Loading…",
        entityHref: subPageLabel && projectId ? nextgenProjectPath(projectId) : undefined,
        sectionLabel: subPageLabel,
      }),
    [project?.name, projectId, subPageLabel]
  );

  const handleOpenShare = useCallback(() => setShareOpen(true), []);
  const handleOpenProjectShare = useCallback(() => setProjectShareOpen(true), []);

  const handleDelete = useCallback(async () => {
    if (!projectId) {
      return;
    }
    try {
      await deleteProject(projectId);
      await mutate(projectListPath());
      navigate(nextgenProjectsListPath());
    } catch {
      showError("Failed to delete project. Please try again.");
    }
  }, [projectId, navigate, showError]);

  // Only surface header actions once the project has loaded: the modals/menus
  // they open are rendered in the success-path return below, so controls shown
  // during load or on the error state would click into nothing.
  const isOwner = project?.access_level === ShareLevel.OWNER;
  const headerActions = useMemo<[React.ReactNode?, React.ReactNode?]>(() => {
    if (!project) {
      return [];
    }
    // Share slot: anyone can share their own session; only owners can share the
    // project itself.
    const shareNode = conversationId ? (
      <ShareButton
        key="share-conversation"
        icon={MessageSquareShare}
        label="Share Session"
        onClick={handleOpenShare}
      />
    ) : isOwner ? (
      <ShareButton key="share-project" label="Share Project" onClick={handleOpenProjectShare} />
    ) : undefined;

    // Gear slot: project settings is just Delete (owner-only). Skip the gear
    // entirely when there's nothing in it.
    const settingsNode = isOwner ? (
      <SettingsMenu key="project-settings">
        <DropdownMenuItem variant="destructive" onClick={() => setDeleteOpen(true)}>
          <Trash2 className="size-4" />
          Delete
        </DropdownMenuItem>
      </SettingsMenu>
    ) : undefined;

    return [shareNode, settingsNode];
  }, [project, conversationId, isOwner, handleOpenShare, handleOpenProjectShare]);

  useNextGenHeader({ breadcrumbs, actions: headerActions });

  if (!projectId) {
    return (
      <div role="alert" className="p-6 text-sm text-destructive">
        Missing project id.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full overflow-hidden">
        <div
          className="w-56 shrink-0 border-r border-border"
          role="status"
          aria-label="Loading project"
        />
        <div className="flex-1" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div
        role="alert"
        className="flex h-full flex-col items-center justify-center gap-3 text-center text-destructive"
      >
        <AlertCircle className="size-8" aria-hidden="true" />
        <p className="text-sm font-medium">Failed to load project</p>
      </div>
    );
  }

  const outletContext: ProjectOutletContext = {
    projectId,
    project,
    isOwner: project.access_level === ShareLevel.OWNER,
    sessions,
    attachedAgents,
    attachedAgentsWithDetails: agentsWithDetails,
    attachedDatasets,
  };

  return (
    <>
      <div className="flex h-full overflow-hidden">
        <ProjectSidebar projectId={projectId} sessions={sessions} />
        <div className="flex flex-1 flex-col overflow-hidden">
          <Outlet context={outletContext} />
        </div>
      </div>
      {conversationId && (
        <ShareConversationDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          conversationId={conversationId}
        />
      )}
      <ShareModal
        open={projectShareOpen}
        onOpenChange={setProjectShareOpen}
        resourceLabel="project"
        resourceName={project.name}
        collaborators={collaborators}
        collaboratorsLoading={collaboratorsLoading}
        collaboratorsError={collaboratorsError}
        accessLevels={accessLevels}
        defaultAccessLevel={defaultAccessLevel}
        onShare={handleShare}
        currentUserEmail={currentUser?.email}
      />
      <DeleteProjectDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        projectName={project.name}
        memberCount={memberCount}
        onConfirm={handleDelete}
      />
    </>
  );
};
